<template>
  <transition
    name="collapse"
    @enter="onEnter"
    @after-enter="onAfterEnter"
    @leave="onLeave"
    @after-leave="onAfterLeave"
  >
    <div v-show="visible" class="ue-collapse" ref="collapse">
      <slot />
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// Collapse component that leverages Vue's <transition> system to animate the height of the content.
// Can be used in conjunction with `useCollapse` composable to manage the state of the collapse.
//
// Usage:
// <UeCollapse :visible="collapsibleComp.visible.value">
//   <div>Content to collapse</div>
// </UeCollapse>
//
// const collapsibleComp = useCollapse(false)
// collapsibleComp.toggle()
export default defineComponent({
  name: 'UeCollapse',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    accordion: {
      type: String,
      default: '',
    },
  },
  emits: ['show', 'shown', 'hide', 'hidden'],
  data() {
    return {
      isTransitioning: false,
    }
  },
  watch: {
    visible(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          this.$emit('show')
        } else {
          this.$emit('hide')
        }
      }
    },
  },
  methods: {
    onEnter(el: HTMLElement) {
      this.isTransitioning = true
      el.style.height = '0'
      el.style.overflow = 'hidden'
      // Force reflow
      el.offsetHeight
      el.style.height = `${el.scrollHeight}px`
    },
    onAfterEnter(el: HTMLElement) {
      this.isTransitioning = false
      el.style.height = 'auto'
      el.style.overflow = ''
      this.$emit('shown')
    },
    onLeave(el: HTMLElement) {
      this.isTransitioning = true
      el.style.height = `${el.scrollHeight}px`
      el.style.overflow = 'hidden'
      // Force reflow
      el.offsetHeight
      el.style.height = '0'
    },
    onAfterLeave(el: HTMLElement) {
      this.isTransitioning = false
      el.style.height = ''
      el.style.overflow = ''
      this.$emit('hidden')
    },
    toggle() {
      this.$emit('update:visible', !this.visible)
    },
    show() {
      this.$emit('update:visible', true)
    },
    hide() {
      this.$emit('update:visible', false)
    },
  },
})
</script>

<style lang="sass" scoped>
// Vue automatically applies these classes during transitions:
// .collapse-enter-active - Applied during the entire entering phase
// .collapse-leave-active - Applied during the entire leaving phase
// .collapse-enter-from - Applied before element insertion, removed after one frame
// .collapse-leave-to - Applied when leaving transition starts

.collapse-enter-active,
.collapse-leave-active
  transition: height 0.3s ease
  overflow: hidden

.collapse-enter-from,
.collapse-leave-to
  height: 0
  overflow: hidden
</style>
